import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabLabel } from '../../components/shell/TabsContext';
import RecordPage from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import { StatusBadge } from '../../components/datagrid/DataGrid';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Clock,
  CheckCircle,
  XCircle,
  CalendarBlank,
  ChartBar,
  FolderSimple,
  File,
  Video,
  Exam,
  Timer,
  BookOpen
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// Helper to format seconds to readable time
const formatTime = (seconds) => {
  if (!seconds || seconds === 0) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

const getNodeIcon = (nodeType) => {
  switch (nodeType) {
    case 'section':
    case 'subsection':
      return FolderSimple;
    case 'exam':
      return Exam;
    case 'video':
      return Video;
    default:
      return File;
  }
};

export default function StudentAnalytics() {
  const { courseId, studentId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [analytics, setAnalytics] = useState(null);
  useTabLabel(analytics?.student_name ? `Analytics: ${analytics.student_name}` : null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [courseId, studentId]);

  const fetchAnalytics = async () => {
    try {
      const res = await api.get(`/courses/${courseId}/students/${studentId}/analytics`);
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load student analytics');
      navigate(`/instructor/courses/${courseId}`);
    } finally {
      setLoading(false);
    }
  };

  const backToCourse = () => navigate(`/instructor/courses/${courseId}`);

  if (loading || !analytics) {
    return (
      <DashboardLayout>
        <RecordPage title={loading ? 'Student analytics' : 'Analytics not found'} loading={loading} onBack={backToCourse} maxWidth="none">
          <p className="text-muted-foreground text-center py-12">Analytics not found</p>
        </RecordPage>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="student-analytics-page"
        title={analytics.student_name}
        subtitle={[analytics.course_title, analytics.student_email].filter(Boolean).join(' · ')}
        badge={<StatusBadge value={analytics.enrollment_status} />}
        onBack={backToCourse}
        onRefresh={fetchAnalytics}
        maxWidth="none"
        secondary={[
          { key: 'course', label: 'View course', icon: BookOpen, onClick: backToCourse },
        ]}
      >
        {/* Summary tiles */}
        <StatTiles
          items={[
            { label: 'Overall progress', value: `${analytics.overall_progress.toFixed(0)}%`, icon: ChartBar },
            { label: 'Total time spent', value: formatTime(analytics.total_time_spent_seconds), icon: Timer },
            { label: 'Lessons completed', value: `${analytics.completed_nodes}/${analytics.total_content_nodes}`, icon: CheckCircle },
            { label: 'Enrolled', value: new Date(analytics.enrolled_at).toLocaleDateString(), icon: CalendarBlank },
          ]}
        />

        {/* Detailed Node Analytics */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Time Spent by Topic
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Topic</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Time Spent</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Accessed</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.node_analytics.map((node) => {
                    const NodeIcon = getNodeIcon(node.node_type);
                    const isSection = node.node_type === 'section' || node.node_type === 'subsection';
                    
                    return (
                      <tr 
                        key={node.node_id} 
                        className={`border-b last:border-0 hover:bg-muted/50 ${isSection ? 'bg-muted/30' : ''}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <NodeIcon className={`w-4 h-4 ${isSection ? 'text-amber-500' : 'text-muted-foreground'}`} />
                            <div>
                              <span className={`text-sm ${isSection ? 'font-semibold' : ''}`}>{node.title}</span>
                              {node.parent_title && (
                                <p className="text-xs text-muted-foreground">in {node.parent_title}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="capitalize text-xs">
                            {node.node_type}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`text-sm font-medium ${node.time_spent_seconds > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                            {formatTime(node.time_spent_seconds)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {!isSection && (
                            node.completed ? (
                              <Badge variant="default" className="bg-emerald-600">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Done
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <XCircle className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {node.last_accessed ? new Date(node.last_accessed).toLocaleString() : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Time Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Timer className="w-5 h-5" />
              Time Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-3xl font-bold text-emerald-600">{formatTime(analytics.total_time_spent_seconds)}</p>
                <p className="text-sm text-muted-foreground mt-1">Total Time Spent</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">
                  {analytics.total_content_nodes > 0 
                    ? formatTime(Math.round(analytics.total_time_spent_seconds / analytics.total_content_nodes))
                    : '-'
                  }
                </p>
                <p className="text-sm text-muted-foreground mt-1">Avg Time per Topic</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-3xl font-bold text-purple-600">
                  {analytics.completed_nodes > 0 
                    ? formatTime(Math.round(analytics.total_time_spent_seconds / analytics.completed_nodes))
                    : '-'
                  }
                </p>
                <p className="text-sm text-muted-foreground mt-1">Avg Time per Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </RecordPage>
    </DashboardLayout>
  );
}
